import React, { useState, useEffect, useContext, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useUrlParams from 'hooks/useUrlParams'
import classNames from 'classnames'
import IconChevron from 'components/partials/IconChevron'
import Checkbox from 'components/Checkbox'
import ClearButton from 'components/ClearButton'
import FacetChipButton from 'components/FacetChipButton'
import DateFilters from 'widgets/DateFilters'
import DrupalContext from 'contexts/DrupalContext'

function Facets({ facets = [] }) {
  const { t } = useTranslation()
  const { urlParams } = useUrlParams()
  const drupal = useContext(DrupalContext)

  const showDateFilters = {
    news: true,
    event: true,
  }[drupal.niche || urlParams.searcher]

  const showMoreFilters = {
    education: 6, // default number of filters to show (date filters not counted)
    exchange_course: 6,
  }[drupal.niche || urlParams.searcher]

  const displayFilterAsButtons = {
    open_for_application: true,
    'education_level.keyword': false,
  }

  const hideFilterInPanel = {
    open_for_application: true
  }

  const [showAllFacets, setShowAllFacets] = useState(false)
  const [insidePanelFacets, setInsidePanelFacets] = useState([])
  const [outsidePanelFacets, setOutsidePanelFacets] = useState([])

  useEffect(() => {
    setShowAllFacets(false)
  }, [urlParams.searcher])

  useEffect(() => {
    setInsidePanelFacets(
      !showAllFacets && showMoreFilters
      ? facets.slice(0, showMoreFilters)
      : facets
    )
    setOutsidePanelFacets(
      facets.filter(x => x.area === "MAIN")
    )
  }, [facets, showAllFacets])

  const getTextWidth = (text, font = '14px acumin-pro,Helvetica,sans-serif') => {
    // re-use canvas object for better performance
    const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'))
    const context = canvas.getContext('2d')
    context.font = font
    const metrics = context.measureText(text)
    return metrics.width
  }

  const facetDropdownWidth = (facet, min = 200, padding = 40 + 32 + 4) => {
    const width = facet.filters
      .concat({ displayName: `${t('Choose')} ${facet.displayName}` }) // include facet placeholder into calculation
      .reduce((acc, cur) => {
        const textWidth = getTextWidth(`${cur.displayName} (${cur.count || ''})`)
        acc = Math.max(acc, Math.ceil(textWidth))
        return acc
      }, 0)
    return Math.max(min, width + padding)
  }

  return (
    <div className='layout layout--gray'>
      {!!facets.length && (
        <div
          className='layout__container layout__container--dynamic u-pb-150 u-pb-200-large-up'
          style={{ height: 'inherit' }}
        >
          <div className='row layout__inner'>
            <DateFilters renderIf={showDateFilters} />

            {insidePanelFacets.map((facet , index) => (
              hideFilterInPanel[facet.id]
                ? ''
                :
                  [ displayFilterAsButtons[facet.id]
                  ? <FacetChipButton facet={facet} />
                  : <Dropdown
                      {...facet}
                      key={facet.id}
                      index={index}
                      maxFilters={showMoreFilters}
                      hideNonSelectable={facet.hideNonSelectable}
                      disabled={!facet.filters.length || facet.disabled && !(facet.enableOnParam in urlParams)}
                      width={facetDropdownWidth(facet)}
                    />
                  ]
            ))}

            <div className='columns small-12 u-mt-150'>
            {outsidePanelFacets.map(facet => (
              <Checkbox
                id={facet.id}
                tabIndex={0}
                value={facet.filters[0].displayName}
                label={`${facet.filters[0].displayName  } (${  facet.filters[0].count  })`}
              />
            ))}
            </div>

            {!!showMoreFilters && !!facets.length && (
              <div className='columns small-12 u-mt-150'>
                <button
                  className={classNames('content-toggle__button', { 'is-expanded': showAllFacets })}
                  aria-expanded={showAllFacets}
                  onClick={() => setShowAllFacets(!showAllFacets)}
                >
                  {showAllFacets ? t('Show less filters') : t('Show more filters')}
                  <IconChevron modifierClasses='u-ml-25' />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Facets

function Dropdown({ id, index, maxFilters, displayName, hideNonSelectable, filters, disabled, width }) {
  const { t } = useTranslation()
  const { urlParams, updateUrlParams } = useUrlParams()
  const [activeIndex, setActiveIndex] = useState(-1)
  const [scrollDirectionUp, setScrollDirectionUp] = useState(false)
  const [showState, setShowState] = useState(false)
  const [facetValue, setFacetValue] = useState()
  const [placeholder, setPlaceholder] = useState(
    `${t('Choose')} ${displayName.toLowerCase()}`
  )
  const _setSelectedFilterValue = () => {
    const selectedFilter = filters.filter(filter => filter.selected)
    if (selectedFilter.length) {
      setFacetValue(selectedFilter[0].displayName)
    } else {
      setFacetValue('')
    }
  }
  const facetLabel = displayName
  const dropDownFacet = useRef()
  const dropDownListFacet = useRef()

  const maxDropdownItems = 7
  const maxDropdownItemsHeight = 34 // The height of items in pixels

  useEffect(() => {
    _setSelectedFilterValue()
  }, [filters])

  const handleBlur = () => {
    setShowState(false)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      setShowState(!showState && !disabled) // open if not disabled, always possible to close
    }

    if (showState) {
      switch (event.key) {
        case 'Enter':
          const { displayName, query } = filters[activeIndex] || {}
          if (query) updateUrlParams({ [id]: displayName })
          return setFacetValue(displayName || '')
        case 'Tab':
          return setShowState(false)
        case 'ArrowDown':
          event.preventDefault();
          setScrollDirectionUp(false);
          return setActiveIndex(activeIndex >= filters.length - 1 ? 0 : activeIndex + 1)
        case 'ArrowUp':
          if (activeIndex <= 0) return false;
          event.preventDefault();
          setScrollDirectionUp(true);
          return setActiveIndex(activeIndex === 0 ? filters.length - 1 : activeIndex - 1)
        case 'Esc': // IE/Edge specific value
        case 'Escape':
          return setShowState(false)
      }
    }
  }

  const handlePick = ({ displayName }) => {
    setFacetValue(displayName)
    setShowState(false)
    updateUrlParams({ [id]: displayName })
  }

  useEffect(() => {
    if (showState) {
      dropDownFacet.current.setAttribute('aria-activedescendant', activeIndex > -1 ? `filter--${facetLabel.replace(/\s+/g, '-').toLowerCase()}__${activeIndex}` : '')
      if (activeIndex % maxDropdownItems === 0 || (activeIndex % 1 === 0 && scrollDirectionUp)) {
        dropDownListFacet.current.scrollTop = activeIndex * maxDropdownItemsHeight
      }
    } else {
      dropDownFacet.current.removeAttribute('aria-activedescendant')
    }
  }, [showState, activeIndex])

  useEffect(() => {
    if (index == maxFilters) {
      dropDownFacet.current.focus();
    }
  }, [index, maxFilters])

  return (
    <div className='column u-max-width-full u-mt-150 u-mt-200-large-up' style={{ flex: `0 0 ${width}px`}}>
      <div
        className='label u-text-uppercase u-mb-50'
        id={displayName.replace(/\s+/g, '-').toLowerCase()}
        aria-hidden={!!disabled}
      >
        {displayName}
      </div>
      <div
        className={classNames('dropdown', 'dropdown--small', {'is-open': showState}, {'is-disabled': disabled})}
        ref={dropDownFacet}
        role='listbox'
        {... (!disabled && { tabIndex: 0 })}
        aria-expanded={showState}
        aria-hidden={!!disabled}
        disabled={!!disabled}
        onClick={() => !disabled && setShowState(!showState)}
        active={!!facetValue}
        onKeyDown={event => handleKeyDown(event)}
        onBlurCapture={handleBlur}
        aria-label={`${t('Choose')} ${displayName.toLowerCase()}`}
      >
        <div
          className='dropdown__inner'
        >
          <div
            className='dropdown__list dropdown__list--single'
          >
            <div className='dropdown__item' {... (!disabled && { role: 'option' })}>
              {facetValue || placeholder}
            </div>
          </div>
        </div>
        {facetValue ? (
          <ClearButton
            modifierClasses='dropdown__clear'
            label={`${t('Clear selection')}`}
            onClick={e => {
              e.stopPropagation()
              setShowState(false)
              setPlaceholder(`${t('Choose')} ${displayName.toLowerCase()}`)
              setFacetValue('')
              updateUrlParams({
                [id]: undefined,
              })
            }}
          />
        ) : (
          <IconChevron />
        )}
        <div
          className={showState ? 'dropdown__list dropdown__list--dropdown is-active' : 'dropdown__list dropdown__list--dropdown'}
        >
          {showState && (
            <ul
              className='dropdown__list'
              ref={dropDownListFacet}
              role='listbox'
              aria-expanded={showState}
            >
              {filters.map(({ displayName, query, count, selected }, index) => (
                <li
                  className={classNames(
                    'dropdown__item dropdown__item--selectable',
                    {'is-disabled': !count},
                    {'is-highlighted': index === activeIndex},
                    {'is-hidden': hideNonSelectable && !count},
                    {'u-font-weight-700': selected},
                  )}
                  role='option'
                  aria-selected={selected}
                  aria-label={displayName}
                  id={`filter--${facetLabel.replace(/\s+/g, '-').toLowerCase()}__${index}`}
                  onClick={(e) => !count ? e.preventDefault() : handlePick({ displayName })}
                >
                  {displayName} ({count})
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
